import { useRef, useState } from "react";
import {API} from "../utils";
import { tokenManager } from "../services/tokenManager";

interface StreamEvent {
    type: "status" | "stream" | "done" | "error";
    content?: string;
    error?: string;
}

export interface ChatMessage {
    role: "user" | "assistant";
    content: string;
}

type UiStatus = "idle" | "thinking" | "typing";

export function useChatStream(apiKey?: string, embedOrigin?: string) {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<UiStatus>("idle");
    const [statusText, setStatusText] = useState<string>("");
    const [limitReached, setLimitReached] = useState(false);
    const controllerRef = useRef<AbortController | null>(null);

    const sendMessage = async (sessionId: string, input: string) => {
        if (!sessionId || !input.trim()) return;
        setMessages((prev) => [...prev, { role: "user", content: input }]);
        setIsStreaming(true);
        setError(null);
        setStatus("thinking");
        setStatusText("Thinking...");

        controllerRef.current = new AbortController();

        let assistantText = "";

        try {
            const headers: Record<string, string> = {
                "Content-Type": "application/json",
                "Cache-Control": "no-cache, no-store, must-revalidate",
            };
            const token = tokenManager.getAccessToken();
            if (token) headers["Authorization"] = `Bearer ${token}`;
            if (apiKey) headers["x-api-key"] = apiKey;
            if (embedOrigin) headers["x-embed-origin"] = embedOrigin;

            const res = await fetch(`${API}/v1/chat/stream`, {
                method: "POST",
                headers,
                body: JSON.stringify({ sessionId, input }),
                signal: controllerRef.current.signal,
            });

            if (res.status === 429) {
                // Remove the optimistic user message we just added
                setMessages((prev) => prev.slice(0, -1));
                setLimitReached(true);
                setIsStreaming(false);
                setStatus("idle");
                setStatusText("");
                return;
            }

            if (!res.ok && res.status !== 200) {
                const body = await res.json().catch(() => ({}));
                const err: any = new Error(body.message || `HTTP ${res.status}`);
                err.status = res.status;
                throw err;
            }

            if (!res.body) throw new Error("No response body");

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const blocks = buffer.split("\n\n");
                buffer = blocks.pop() || "";

                for (const block of blocks) {
                    const lines = block.split("\n");

                    for (const line of lines) {
                        if (!line.startsWith("data:")) continue;

                        const json = line.replace("data:", "").trim();
                        if (!json) continue;

                        const parsed: StreamEvent = JSON.parse(json);

                        switch (parsed.type) {
                            case "status":
                                setStatus("thinking");
                                setStatusText(parsed.content || "Thinking...");
                                break;

                            case "stream":
                                if (parsed.content) {
                                    setStatus("typing");
                                    setStatusText("");

                                    assistantText += parsed.content;

                                    setMessages((prev) => {
                                        const copy = [...prev];
                                        const last = copy[copy.length - 1];

                                        if (last?.role === "assistant") {
                                            copy[copy.length - 1] = {
                                                role: "assistant",
                                                content: assistantText,
                                            };
                                        } else {
                                            copy.push({ role: "assistant", content: assistantText });
                                        }

                                        return copy;
                                    });
                                }
                                break;

                            case "done":
                                break;

                            case "error":
                                throw new Error(parsed.error || "Stream error");

                            default:
                                if (parsed.error) throw new Error(parsed.error);
                                break;
                        }
                    }
                }
            }
        } catch (e: any) {
            if (e.name !== "AbortError") {
                setError(e.message || "Stream error");
                // Re-throw 404 so caller can handle session recreation
                if (e.status === 404) throw e;
            }
        } finally {
            setIsStreaming(false);
            controllerRef.current = null;
            setStatus("idle");
            setStatusText("");
        }
    };

    const abort = () => {
        controllerRef.current?.abort();
        setIsStreaming(false);
        setStatus("idle");
        setStatusText("");
    };

    const clear = () => {
        setMessages([]);
        setError(null);
        setStatus("idle");
        setStatusText("");
    };

    const resetLimit = () => setLimitReached(false);

    return {
        messages,
        isStreaming,
        error,
        status,
        statusText,
        limitReached,
        sendMessage,
        abort,
        clear,
        resetLimit,
    };
}
